import { Suspense } from 'react';
import type { JSX, ReactNode } from 'react';
import { lazyWithRetry } from '@/lib/lazy-with-retry';
import { ChunkErrorBoundary } from '@/common/components/ChunkErrorBoundary';
import { ModulePaneSkeleton } from '@/common/components/ModulePaneSkeleton';
import TimelineModule from '@/modules/calendar/TimelineModule';
import PlaceListModule from '@/modules/places/PlaceListModule';
import MapModule from '@/modules/places/MapModule';
import CalendarModule from '@/modules/calendar/CalendarModule';
import PlaceCardModule from '@/modules/places/PlaceDetailModule';
import EventListModule from '@/modules/calendar/EventListModule';
import { NewsPanel } from '@/modules/news/NewsPanel';
import { ShopifyCRMPanel } from '@/modules/shopify-crm/ShopifyCRMPanel';
import { AppAnalyticsPanel } from '@/modules/mixpanel/AppAnalyticsPanel';
import HealthContainerModule from '@/modules/health/HealthContainerModule';
import AutomationsModule from '@/modules/automations/AutomationsModule';
import NotesModule from '@/modules/notes/NotesModule';
import SynthesisModule from '@/modules/synthesis/SynthesisModule';
import type { ModuleRendererProps } from '../types';

const ChannelsModule = lazyWithRetry(() => import('@/modules/channels/ChannelsModule'));

// Campaigns — entitlement-gated at render time (CampaignsModule checks the
// `campaigns` grant itself via useEntitlement; ADR 042 §13)
const LazyCampaignsModule = lazyWithRetry(() =>
  import('@/modules/campaigns/CampaignsModule').then((m) => ({ default: m.CampaignsModule })),
);

// Lazy-load EmailPanel to break circular dep: EmailPanel → ConversationContext → pluginApi
const LazyEmailPanel = lazyWithRetry(() => import('@/modules/email/EmailPanel').then(m => ({ default: m.EmailPanel })));

// The heaviest feature areas are code-split out of the eager bundle: Drive
// (TipTap editor stack), Tasks (@dnd-kit + task-import-export), Contacts
// (pipeline drag-drop). Cold-render latency is covered by chunk prefetch at
// every entry point — rail hover + idle warmup (SidebarRail), module.focus UI
// actions (apply-ui-actions), and the new-module picker (useModuleCreation) —
// all via LAZY_CHUNK_FACTORIES in lib/prefetch-module.ts; keep that map in
// sync when adding/removing entries here.
const LazyDriveModule = lazyWithRetry(() => import('@/modules/drive/DriveModule'));
// Pages/Report/HtmlViewer share the studio + report stack (recharts via
// common/blocks) — code-split so charts never load at boot (#5255).
const LazyPagesContainerModule = lazyWithRetry(() => import('@/modules/pages/PagesContainerModule'));
const LazyReportModule = lazyWithRetry(() => import('@/modules/pages/report/ReportModule').then((m) => ({ default: m.ReportModule })));
const LazyHtmlViewerModule = lazyWithRetry(() => import('@/modules/pages/artifacts/HtmlViewerModule'));
const LazyDocGridModule = lazyWithRetry(() => import('@/modules/drive/docs/DocGridModule'));
const LazyFileViewerModule = lazyWithRetry(() => import('@/modules/drive/FileViewerModule'));
const LazyContactListModule = lazyWithRetry(() => import('@/modules/contacts/ContactListModule'));
const LazyClientListModule = lazyWithRetry(() => import('@/modules/contacts/clients/ClientListModule'));
const LazyKanbanModule = lazyWithRetry(() => import('@/modules/kanban/KanbanModule'));

/**
 * Chunk-loading shell shared by every code-split registry entry: an error
 * boundary for failed chunk loads plus a shape-matched skeleton while the
 * chunk streams in.
 */
function LazyPane({ label, variant = 'list', children }: { label: string; variant?: 'list' | 'grid'; children: ReactNode }) {
  return (
    <ChunkErrorBoundary label={label}>
      <Suspense fallback={<ModulePaneSkeleton variant={variant} />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}

/**
 * Per-pane error boundary for eagerly-bundled registry entries. Without it
 * the only boundary above a module is the root full-screen ErrorBoundary,
 * so a render-time throw in one pane would blank the whole app.
 */
function EagerPane({ label, children }: { label: string; children: ReactNode }) {
  return <ChunkErrorBoundary label={label}>{children}</ChunkErrorBoundary>;
}

export const MODULE_COMPONENTS: Record<string, (props: ModuleRendererProps) => JSX.Element> = {
  // Primary modules
  task_list: ({ module, conversationId }) => (
    <LazyPane label="Boards">
      <LazyKanbanModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  event_list: ({ module, conversationId, filterText }) => (
    <EagerPane label="Events">
      <EventListModule module={module} conversationId={conversationId || ''} filterText={filterText} />
    </EagerPane>
  ),
  contact_list: ({ module, conversationId }) => (
    <LazyPane label="Contacts">
      <LazyContactListModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  client_list: ({ module, conversationId }) => (
    <LazyPane label="Clients">
      <LazyClientListModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  places: ({ module, conversationId }) => (
    <EagerPane label="Places">
      <PlaceListModule module={module} conversationId={conversationId || ''} />
    </EagerPane>
  ),
  place_list: ({ module, conversationId }) => (
    <EagerPane label="Places">
      <PlaceListModule module={module} conversationId={conversationId || ''} />
    </EagerPane>
  ),
  drive: ({ module, conversationId }) => (
    <LazyPane label="Drive" variant="grid">
      <LazyDriveModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  // Supplemental
  note_grid: ({ module, conversationId }) => (
    <LazyPane label="Docs" variant="grid">
      <LazyDocGridModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  // kanban: multi-board kanban module with board browser
  kanban: ({ module, conversationId }) => (
    <LazyPane label="Boards">
      <LazyKanbanModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  // kanban_board: alias kept for any modules created under the old name
  kanban_board: ({ module, conversationId }) => (
    <LazyPane label="Boards">
      <LazyKanbanModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  // crm type now uses ContactListModule
  crm: ({ module, conversationId }) => (
    <LazyPane label="Contacts">
      <LazyContactListModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  map: ({ module, conversationId }) => (
    <EagerPane label="Map">
      <MapModule module={module} conversationId={conversationId || ''} />
    </EagerPane>
  ),
  calendar: ({ module, conversationId }) => (
    <EagerPane label="Calendar">
      <CalendarModule module={module} conversationId={conversationId || ''} />
    </EagerPane>
  ),
  file_viewer: ({ module, conversationId }) => (
    <LazyPane label="File viewer">
      <LazyFileViewerModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  place_card: ({ module, conversationId }) => (
    <EagerPane label="Place">
      <PlaceCardModule module={module} conversationId={conversationId || ''} />
    </EagerPane>
  ),
  timeline: ({ module, conversationId, filterText }) => (
    <EagerPane label="Timeline">
      <TimelineModule module={module} conversationId={conversationId || ''} filterText={filterText} />
    </EagerPane>
  ),

  // Plugins / integrations rendered as modules
  email: ({ module, conversationId }) => (
    <LazyPane label="Email">
      <LazyEmailPanel
        config={(module.config ?? {}) as Record<string, string>}
        onOpenConfig={() => {}}
        conversationId={conversationId || ''}
        instanceId={module.id}
      />
    </LazyPane>
  ),
  news: ({ module, conversationId }) => (
    <EagerPane label="News">
      <NewsPanel
        config={(module.config ?? { instructions: 'Top news across politics, markets, tech, and world events', tickers: 'SPX,NDX,GC,BTC,ETH' }) as Record<string, string>}
        onOpenConfig={() => {}}
        conversationId={conversationId || ''}
        instanceId={module.id}
      />
    </EagerPane>
  ),
  channels: ({ module, conversationId }) => (
    <LazyPane label="Channels">
      <ChannelsModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),
  'shopify-crm': ({ module, conversationId }) => (
    <EagerPane label="Shopify CRM">
      <ShopifyCRMPanel
        config={(module.config ?? {}) as Record<string, string>}
        onOpenConfig={() => {}}
        conversationId={conversationId || ''}
        instanceId={module.id}
      />
    </EagerPane>
  ),
  'app-analytics': ({ module, conversationId }) => (
    <EagerPane label="Analytics">
      <AppAnalyticsPanel
        config={(module.config ?? {}) as Record<string, string>}
        onOpenConfig={() => window.dispatchEvent(new CustomEvent('xavior:open-mixpanel-config', { detail: { moduleId: module.id } }))}
        conversationId={conversationId || ''}
        instanceId={module.id}
      />
    </EagerPane>
  ),
  mixpanel: ({ module, conversationId }) => (
    <EagerPane label="Analytics">
      <AppAnalyticsPanel
        config={(module.config ?? {}) as Record<string, string>}
        onOpenConfig={() => window.dispatchEvent(new CustomEvent('xavior:open-mixpanel-config', { detail: { moduleId: module.id } }))}
        conversationId={conversationId || ''}
        instanceId={module.id}
      />
    </EagerPane>
  ),
  // Pages container — lists all report and html_viewer modules
  pages: ({ module, conversationId }) => (
    <LazyPane label="Pages" variant="grid">
      <LazyPagesContainerModule module={module} conversationId={conversationId || ''} />
    </LazyPane>
  ),

  // Health container — timeline of health events (metrics, documents)
  health: ({ module, conversationId }) => (
    <EagerPane label="Health">
      <HealthContainerModule module={module} conversationId={conversationId || ''} />
    </EagerPane>
  ),

  // Artifact viewer — sandboxed iframe for agent-generated HTML
  html_viewer: ({ module, conversationId }) => (
    <LazyPane label="Viewer">
      <LazyHtmlViewerModule module={module} conversationId={conversationId} />
    </LazyPane>
  ),

  // Report — AI-generated editorial reports
  report: ({ module }) => (
    <LazyPane label="Report">
      <LazyReportModule module={module} />
    </LazyPane>
  ),

  // Automations — launch automations via /agent-jobs (legacy 'agents' type
  // resolves here through MODULE_ALIASES → 'automations').
  automations: ({ module, conversationId }) => (
    <EagerPane label="Automations">
      <AutomationsModule module={module} conversationId={conversationId || ''} />
    </EagerPane>
  ),
  notes: ({ module, conversationId }) => (
    <EagerPane label="Notes">
      <NotesModule module={module} conversationId={conversationId || ''} />
    </EagerPane>
  ),
  synthesis: ({ module, conversationId }) => (
    <EagerPane label="Synthesis">
      <SynthesisModule module={module} conversationId={conversationId || ''} />
    </EagerPane>
  ),

  // Campaigns — workspace-level flag-gated; CampaignsModule renders null if disabled
  campaigns: () => (
    <LazyPane label="Campaigns">
      <LazyCampaignsModule />
    </LazyPane>
  ),
};
